import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { RootState } from '@/store'

export interface TextToSpeechItem {
  text: string
  element?: HTMLElement | null
  elementId?: string
  utterance?: SpeechSynthesisUtterance,
  locale: string
}

export class TextToSpeechState {
  currentIndex: number = 0
  current: TextToSpeechItem | null = null
  list: TextToSpeechItem[] = []
  readAllItems: boolean = false
  isEnabled: boolean = false
  isStarted: boolean = false
  isPaused: boolean = false
  ignoreAutoNext: boolean = false
  enableOnUserSettings: boolean = false
}

export const GET_CURRENT = 'text-to-speech/GET_CURRENT'
export const GET_LIST = 'text-to-speech/GET_LIST'
export const ADD_ITEM = 'text-to-speech/ADD_ITEM'
export const REMOVE_ITEM = 'text-to-speech/REMOVE_ITEM'
export const CLICK_ON_ITEM = 'text-to-speech/READ_ITEM'
export const RESET = 'text-to-speech/RESET'
export const SET_READ_ALL_ITEMS = 'text-to-speech/SET_READ_ALL_ITEMS'
export const SET_INDEX = 'text-to-speech/SET_INDEX'
export const SET_IS_ENABLED = 'text-to-speech/SET_IS_ENABLED'
export const SET_IS_STARTED = 'text-to-speech/SET_IS_STARTED'
export const SET_IS_PAUSED = 'text-to-speech/SET_IS_PAUSED'
export const READ_LIST = 'text-to-speech/READ_LIST'
export const READ_ITEM_FROM_INDEX = 'text-to-speech/READ_ITEM_FROM_INDEX'
export const TOGGLE_ITEM_FROM_INDEX = 'text-to-speech/TOGGLE_ITEM_FROM_INDEX'
export const GET_IS_ENABLED = 'text-to-speech/GET_IS_ENABLED'
export const GET_IS_STARTED = 'text-to-speech/GET_IS_STARTED'
export const GET_IS_PAUSED = 'text-to-speech/GET_IS_PAUSED'
export const PAUSE = 'text-to-speech/PAUSE'
export const RESUME = 'test-to-speech/RESUME'
export const STOP = 'text-to-speech/STOP'
export const PLAY = 'text-to-speech/PLAY'
export const HAS_NEXT = 'text-to-speech/HAS_NEXT'
export const HAS_PREV = 'text-to-speech/HAS_PREV'
export const NEXT = 'text-to-speech/NEXT'
export const PREV = 'text-to-speech/PREV'
export const SET_IGNORE_AUTO_NEXT = 'text-to-speech/SET_IGNORE_AUTO_NEXT'
export const GET_IGNORE_AUTO_NEXT = 'text-to-speech/GET_IGNORE_AUTO_NEXT'
export const READ_NEXT_ITEM = 'text-to-speech/READ_NEXT_ITEM'
export const SHOW_CONTROLS = 'text-to-speech/SHOW_CONTROLS'
export const ENABLE_ON_USER_SETTINGS = 'text-to-speech/ENABLE_ON_USER_SETTINGS'

const state = new TextToSpeechState()

const getElement = (item: TextToSpeechItem) => {
  if (!item) return null
  return item.element || (item.elementId && document.getElementById(item.elementId)) || null
}

const chunkText = (txt: string) => {
  const chunkLength = 160;
  const pattRegex = new RegExp('^[\\s\\S]{' + Math.floor(chunkLength / 2) + ',' + chunkLength + '}[.!?,]{1}|^[\\s\\S]{1,' + chunkLength + '}$|^[\\s\\S]{1,' + chunkLength + '} ');
  const chunkArr = txt.match(pattRegex);
  if (!chunkArr) return null;
  return chunkArr[0];
}

const splitLongText = (src: string) => {
  const arr: string[] = []
  let txt = src
  while (txt.length > 1) {
    const part = chunkText(txt)
    if (!part) txt = ''
    else {
      arr.push(part)
      txt = txt.replace(part, '')
    }
  }
  return arr
}

const createUtterancesFromString = (text: string, locale: string) => {
  // TODO, split text with end of sentence before chunking by length
  try {
    const textArr = splitLongText(text)
    if (!textArr.length || (textArr.length === 1 && textArr[0].length < 1)) return []
    const uttArr: SpeechSynthesisUtterance[] = []
    textArr.forEach((str: string) => {
      const utt = new SpeechSynthesisUtterance()
      utt.lang = locale
      utt.volume = 1
      utt.rate = 1
      utt.pitch = 1
      utt.text = str
      uttArr.push(utt)
    })
    return uttArr
  } catch (e) {
    console.error(`Error during createUtteranceAndSpeak ${e}`)
    return []
  }
  
}

const getters: GetterTree<TextToSpeechState, RootState> = {
  [GET_CURRENT]: (state): TextToSpeechItem | null => state.list[state.currentIndex],
  [GET_LIST]: (state): TextToSpeechItem[] => state.list,
  [GET_IS_PAUSED]: (state): boolean => state.isPaused,
  [GET_IS_ENABLED]: (state): boolean => state.isEnabled,
  [GET_IS_STARTED]: (state): boolean => state.isStarted,
  [HAS_NEXT]: (state): boolean => state.list.length > state.currentIndex + 1,
  [HAS_PREV]: (state): boolean => state.currentIndex > 0,
  [GET_IGNORE_AUTO_NEXT]: (state): boolean => state.ignoreAutoNext,
  [SHOW_CONTROLS]: (state): boolean => state.isStarted || state.readAllItems,
  [ENABLE_ON_USER_SETTINGS]: (state): boolean => state.enableOnUserSettings
}

const actions: ActionTree<TextToSpeechState, RootState> = {
  [ADD_ITEM] ({ state, commit, dispatch }, payload: {
    element: HTMLElement | null,
    elementId: string,
    text: string,
    locale: string
  }) {
    const { text, element, elementId, locale } = payload
    if (!text || !locale) return
    if (!state.list.find((item: TextToSpeechItem) => item.text === payload.text)) {
      commit(ADD_ITEM, { text, element, elementId, locale })
    }
  },
  [READ_NEXT_ITEM] ({ state, commit, dispatch}) {
    if (state.readAllItems && !state.ignoreAutoNext) dispatch(READ_ITEM_FROM_INDEX, state.currentIndex + 1)
    // else commit(SET_IS_PAUSED, true)
  },
  [READ_ITEM_FROM_INDEX] ({ state, commit, dispatch }, index: number) {
    if (index < 0 || index >= state.list.length) return // dispatch(RESET)
    window.speechSynthesis.cancel()
    const item = state.list[index]
    if (item.text.length < 1) return
    if (state.currentIndex !== index) getElement(state.list[state.currentIndex])?.classList.remove('tts-is-playing')

    const uttArr = createUtterancesFromString(item.text, item.locale)
    if (!uttArr || uttArr.length === 0 || !uttArr[0]) return dispatch(STOP)
    commit(SET_INDEX, index)
    
    uttArr[0].onstart = () => {
      commit(SET_INDEX, index)
      commit(SET_IS_STARTED, true)
      commit(SET_IS_PAUSED, false)
      commit(SET_IGNORE_AUTO_NEXT, false)
      getElement(state.list[index])?.classList.add('tts-is-playing')
    }
    uttArr[uttArr.length - 1].onend = () => { 
      // commit(SET_IS_STARTED, false)
      commit(SET_IS_PAUSED, true)
      dispatch(READ_NEXT_ITEM)
    }

    uttArr.forEach((utt: SpeechSynthesisUtterance) => {
      console.info('[INFO][TTS] Read utterance :', utt)
      utt.onpause = () => { commit(SET_IS_PAUSED, true) }
      utt.onresume = () => { commit(SET_IS_PAUSED, false) }
      setTimeout(() => {
        speechSynthesis.speak(utt);
    }, 0);
    })
    commit(SET_IS_PAUSED, false)
    commit(SET_IS_STARTED, true)
  },
  [TOGGLE_ITEM_FROM_INDEX] ({ state, commit, dispatch }, index: number) {
    if (!window.speechSynthesis.speaking || state.currentIndex !== index) return dispatch(READ_ITEM_FROM_INDEX, index)
    if (state.isStarted) return dispatch(state.isPaused ? RESUME : PAUSE)
    dispatch(READ_ITEM_FROM_INDEX, index)
  },
  [CLICK_ON_ITEM] ({ state, commit, dispatch }, text: string) {
    if (!state.enableOnUserSettings && !state.readAllItems) return
    const index = state.list.findIndex((item: TextToSpeechItem) => item.text === text)
    commit(SET_IGNORE_AUTO_NEXT, true)
    dispatch(TOGGLE_ITEM_FROM_INDEX, index)
  },
  [READ_LIST] ({ state, commit, dispatch }) {
    commit(SET_READ_ALL_ITEMS, true)
    dispatch(READ_ITEM_FROM_INDEX, 0)
  }, 
  [RESET] ({ state, commit }) {
    window.speechSynthesis.cancel()
    state.list.forEach((item: TextToSpeechItem) => getElement(item)?.classList.remove('tts-is-playing'))
    commit(RESET)
  },
  [PLAY] ({ state, dispatch }) {
    if (!state.isStarted) return
    if (state.isPaused) return dispatch(RESUME)
    dispatch(READ_ITEM_FROM_INDEX, state.currentIndex)
  },
  [PAUSE] ({ commit }) {
    window.speechSynthesis.pause()
    commit(SET_IS_PAUSED, true)
    // Check if the current tts is realy paused. else cancel the current tts
    setTimeout(() => {
      if (!window.speechSynthesis.paused) {
        window.speechSynthesis.cancel()
      }
    }, 500)
  },
  [RESUME] ({ commit, dispatch }) {
    if (window.speechSynthesis.paused) window.speechSynthesis.resume()
    else dispatch(READ_ITEM_FROM_INDEX, state.currentIndex)
    commit(SET_IS_PAUSED, false)
    // Check if the current tts is not realy paused. else cancel the current tts
    setTimeout(() => {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.cancel()
        commit(SET_IS_STARTED, false)
        commit(SET_IS_PAUSED, false)
      }
    }, 500)
  },
  [STOP] ({ commit }) {
    commit(SET_READ_ALL_ITEMS, false)
    window.speechSynthesis.cancel()
    commit(SET_IS_PAUSED, false)
    commit(SET_IS_STARTED, false)
    commit(SET_INDEX, 0)
    commit(SET_IS_ENABLED, false)
  },
  [NEXT] ({ state, commit, dispatch }) {
    if (!HAS_NEXT) return
    window.speechSynthesis.cancel()
    commit(SET_IGNORE_AUTO_NEXT, true)
    dispatch(READ_ITEM_FROM_INDEX, state.currentIndex + 1)
  },
  [PREV] ({ state, commit, dispatch }) {
    if (!HAS_PREV) return
    window.speechSynthesis.cancel()
    commit(SET_IGNORE_AUTO_NEXT, true)
    dispatch(READ_ITEM_FROM_INDEX, state.currentIndex - 1)
  }
}

const mutations: MutationTree<TextToSpeechState> = {
  [ADD_ITEM] (state, item: TextToSpeechItem) {
    state.list.push(item)
  }, 
  [REMOVE_ITEM] (state, text: string) {
    const index = state.list.findIndex((item: TextToSpeechItem) => item.text === text)
    state.list.splice(index, 1)
  },
  [RESET] (state) {
    state.list = []
    state.currentIndex = 0
    state.readAllItems = false
    state.isEnabled = false
    state.isStarted = false
    state.isPaused = false
  },
  [SET_READ_ALL_ITEMS] (state, value: boolean) {
    state.readAllItems = value
  },
  [SET_INDEX] (state, index: number)  {
    state.currentIndex = index
  },
  [SET_IS_ENABLED] (state, value: boolean) {
    state.isEnabled = value
  },
  [SET_IS_STARTED] (state, value: boolean) {
    state.isStarted = value
    if (value) state.isEnabled = true
    if (!value) state.isPaused = false
    state.list.forEach((item: TextToSpeechItem, i: number) => {
      if (state.currentIndex === i && state.isStarted) getElement(item)?.classList.add('tts-is-playing')
      else getElement(item)?.classList.remove('tts-is-playing')
    })
  },
  [SET_IS_PAUSED] (state, value: boolean) {
    state.isPaused = value
    state.ignoreAutoNext = false
  },
  [SET_IGNORE_AUTO_NEXT] (state, value: boolean) {
    state.ignoreAutoNext = value
  },
  [ENABLE_ON_USER_SETTINGS] (state, value: boolean) {
    state.enableOnUserSettings = value
  }
}

export const textToSpeech: Module<TextToSpeechState, RootState> = {
  state,
  getters,
  mutations,
  actions
}
